<!-- src/lib/components/Avatar.svelte -->
<!--
  Deterministic avatar. Generates an oklch color from the user's
  name hash so "Cash Conway" always lands on the same swatch, and
  renders the first one or two initials on top. No API, no image
  fetch — purely client-side.
-->
<script lang="ts">
  interface Props {
    /** Displayed name — hashed to pick a stable color. */
    name: string | null | undefined;
    /** Diameter in px. Default 20 — fits inline on a card row. */
    size?: number;
    /** Optional title/tooltip override; defaults to the full name. */
    title?: string;
  }
  let { name = '', size = 20, title }: Props = $props();

  // Tiny string hash (djb2) — stable, no collisions worth worrying
  // about at the scale of a team.
  function hash(s: string): number {
    let h = 5381;
    for (let i = 0; i < s.length; i++) {
      h = ((h << 5) + h) + s.charCodeAt(i);
    }
    return Math.abs(h);
  }

  // Initials: first letter of first two name parts, uppercased.
  const initials = $derived((() => {
    const n = (name ?? '').trim();
    if (!n) return '·';
    const parts = n.split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  })());

  // Hue hand-picked to land in the purple-blue-teal arc that matches
  // the living-UI palette — not true random, just stable bucketing.
  const color = $derived((() => {
    const hue = (hash(name ?? '') % 120) + 230;  // 230°–350° (blue→pink)
    return `oklch(0.62 0.22 ${hue})`;
  })());

  const fg = $derived('oklch(0.97 0.02 310)');
</script>

<span
  class="avatar"
  style="
    --size: {size}px;
    --bg: {color};
    --fg: {fg};
  "
  title={title ?? name ?? ''}
  aria-label={title ?? name ?? 'unassigned'}
>
  <span class="initials">{initials}</span>
</span>

<style>
  .avatar {
    display: inline-flex;
    width:  var(--size);
    height: var(--size);
    border-radius: 50%;
    background: var(--bg);
    color: var(--fg);
    align-items: center;
    justify-content: center;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: calc(var(--size) * 0.42);
    font-weight: 600;
    letter-spacing: 0.02em;
    user-select: none;
    flex-shrink: 0;
    border: 1px solid oklch(0.97 0.02 310 / 0.12);
    line-height: 1;
  }
  .initials {
    display: inline-block;
    transform: translateY(0.5px);
  }
</style>
